import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import logo from "../assets/images/eb_logo.png";
import { API_CONFIG, API_ENDPOINTS } from "../constants/apiConfig";

interface ResetPasswordResponse {
  message: string;
  success: boolean;
}

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract token from URL query parameters
  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
  if (errorMessage) {
    const timer = setTimeout(() => {
      setErrorMessage("");
    }, 3000); // 3 seconds

    return () => clearTimeout(timer);
  }
}, [errorMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate inputs
    if (!password || !confirmPassword) {
      setErrorMessage("Please fill in both password fields.");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    if (!token) {
      setErrorMessage("Invalid or missing reset token.");
      return;
    }

    setErrorMessage("");
    setIsLoading(true);

    try {
      console.log('Sending reset password request...');
      const response = await axios.post<ResetPasswordResponse>(
        `${API_CONFIG.BASE_URL}${API_ENDPOINTS.AUTH.COMPLETE_RESET}`,
        {
          token,
          new_password: password
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('API Response:', response.data);
      
      // Set success state regardless of the response.success value
      // since some APIs might not include it
      setSuccess(true);
      setErrorMessage('');
      
      // Redirect to login after a delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      console.error('Reset password error:', error);
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage = axiosError.response?.data?.message || "Failed to reset password. Please try again.";
      setErrorMessage(errorMessage);
      setSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };
  

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f5f7fb",
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 400,
          p: 4,
          borderRadius: 2,
        }}
      >
        <img src={logo} style={{ width: "30%" }} />

        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ mb: 3, textAlign: "center", mt: 2 }}
        >
          Reset Password
        </Typography>

        {/* ========= MUI ERROR ALERT ========= */}
      {errorMessage && (
  <Alert
    variant="filled" 
    severity="error"
    sx={{
      mb: 2,
      borderRadius: "8px",
      color: "#ffffff",
      bgcolor: "#d32f2f",
      "& .MuiAlert-icon": {
        color: "#ffffff",
      },
      "& .MuiAlert-message": {
        width: '100%',
      }
    }}
  >
    {errorMessage}
  </Alert>
)}

        {success ? (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 3,
              bgcolor: '#4caf50',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white'
              }
            }}
          >
            Password has been reset successfully! Redirecting to login...
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* PASSWORD */}
            <Box>
              <Typography
                variant="caption"
                display="block"
                sx={{ mb: 0.5, color: "#3A3541", textAlign: "left" }}
              >
                Password
              </Typography>

              <TextField
                fullWidth
                size="small"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      sx={{ color: "#3A3541" }}
                    >
                      {showPassword ? <VisibilityOff sx={{ color: "#3A3541" }}/> : <Visibility sx={{ color: "#3A3541" }}/>}
                    </IconButton>
                  ),
                  sx: {
                    bgcolor: "#f5f7fb",
                    borderRadius: "6px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    fontSize: "1rem",
                    color: "#3A3541",
                  },
                }}
              />
            </Box>

            {/* CONFIRM PASSWORD */}
            <Box>
              <Typography
                variant="caption"
                display="block"
                sx={{ mb: 0.5, color: "#3A3541", textAlign: "left" }}
              >
                Confirm Password
              </Typography>

              <TextField
                fullWidth
                size="small"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword((prev) => !prev)
                      }
                      edge="end"
                      sx={{ color: "#3A3541" }}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOff sx={{ color: "#3A3541" }}/>
                      ) : (
                        <Visibility sx={{ color: "#3A3541" }}/>
                      )}
                    </IconButton>
                  ),
                  sx: {
                    bgcolor: "#f5f7fb",
                    borderRadius: "6px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    fontSize: "1rem",
                    color: "#3A3541",
                  },
                }}
              />
            </Box>

            {/* SUBMIT BUTTON */}
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              fullWidth
              sx={{
                bgcolor: "#0b79c1",
                "&:hover": { 
                  bgcolor: "#096aa7",
                },
                "&.Mui-disabled": {
                  bgcolor: "#e0e0e0",
                  color: "#9e9e9e"
                },
                py: 1.5,
                textTransform: "none",
                borderRadius: 1,
                mt: 1,
                mb: 1,
                fontSize: '1rem',
                fontWeight: 500,
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Reset Password'
              )}
            </Button>
          </Stack>
        </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPassword;
